import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  createMultiParser,
  parseAsInteger,
  parseAsNativeArrayOf,
  parseAsString,
  parseAsStringLiteral,
  useQueryStates,
} from 'nuqs';
import { useDebounce } from '../useDebounce';
import type {
  RehospitalizationMode,
  RehospitalizationSortBy,
  ReportPageSize,
  SortOrder,
} from '../../services/api/types.gen';
import {
  coerceReportPageSize,
  isInvalidDateRange,
} from '../../utils/reportDisplay';
import {
  createReportSortParser,
  parseAsReportDate,
  parseAsReportSortDirection,
} from './reportQueryParsers';

export const REPORT_KEYS = [
  'mode',
  'from',
  'to',
  'clinic',
  'diagnosis',
  'sort',
  'dir',
  'page',
  'size',
] as const;

export const MODES = ['current', 'date_range'] as const;
export const DATE_RANGE_MODE: RehospitalizationMode = 'date_range';
export const CURRENT_MODE: RehospitalizationMode = 'current';

export const SORT_FIELDS = [
  'clinic',
  'patient',
  'transfer_date',
  'reason',
  'destination',
  'episode_status',
] as const satisfies readonly RehospitalizationSortBy[];

const nativeDiagnosisArrayParser = parseAsNativeArrayOf(parseAsString);
export const parseAsRehospitalizationDiagnosis = createMultiParser({
  parse: (value: readonly string[]) => {
    const parsed = nativeDiagnosisArrayParser.parse(value);
    if (!parsed) return null;
    const cleaned = parsed
      .flatMap(d => d.split(',').map(s => s.trim()))
      .filter(Boolean);
    return cleaned.length > 0 ? cleaned : null;
  },
  serialize: (value: string[]) => nativeDiagnosisArrayParser.serialize(value),
}).withDefault([]);

const rehospitalizationParsers = {
  mode: parseAsStringLiteral(MODES).withDefault(CURRENT_MODE),
  from: parseAsReportDate,
  to: parseAsReportDate,
  clinic: parseAsString.withDefault(''),
  diagnosis: parseAsRehospitalizationDiagnosis,
  sort: createReportSortParser(SORT_FIELDS),
  dir: parseAsReportSortDirection,
  page: parseAsInteger.withDefault(1),
  size: parseAsInteger.withDefault(25),
};

export const parseDiagnosisInputCodes = (value: string): string[] =>
  value
    .split(',')
    .map(c => c.trim())
    .filter(Boolean);

const areDiagnosisCodesEqual = (
  a: readonly string[],
  b: readonly string[]
): boolean => {
  if (a.length !== b.length) return false;
  return a.every((code, idx) => code === b[idx]);
};

export interface RehospitalizationReportParams {
  mode: RehospitalizationMode;
  dateFrom: string | null;
  dateTo: string | null;
  clinicId: string;
  diagnosis: string[];
  sortBy?: RehospitalizationSortBy;
  sortOrder?: SortOrder;
  page: number;
  pageSize: ReportPageSize;
}

export interface RehospitalizationExportQuery {
  mode: RehospitalizationMode;
  date_from?: string;
  date_to?: string;
  clinic_id?: string;
  diagnosis?: string[];
  sort_by?: RehospitalizationSortBy;
  sort_order?: SortOrder;
}

export interface RehospitalizationTableQuery extends RehospitalizationExportQuery {
  page: number;
  page_size: ReportPageSize;
}

export interface UseRehospitalizationReportParamsResult {
  params: RehospitalizationReportParams;
  tableQuery: RehospitalizationTableQuery;
  exportQuery: RehospitalizationExportQuery;
  diagnosisInput: string;
  setDiagnosisInput: (value: string) => void;
  isDiagnosisPending: boolean;
  dateValidationError: string | null;
  isValidRequest: boolean;
  isBlocked: boolean;
  setMode: (mode: RehospitalizationMode) => Promise<URLSearchParams>;
  setDateRange: (
    from: string | null,
    to: string | null
  ) => Promise<URLSearchParams>;
  setClinicId: (clinicId: string | null) => Promise<URLSearchParams>;
  setSorting: (
    sortBy?: RehospitalizationSortBy,
    sortOrder?: SortOrder
  ) => Promise<URLSearchParams>;
  setPagination: (page: number, pageSize: number) => Promise<URLSearchParams>;
  reset: () => Promise<URLSearchParams>;
}

export function useRehospitalizationReportParams(): UseRehospitalizationReportParamsResult {
  const [urlParams, setUrlParams] = useQueryStates(rehospitalizationParsers, {
    history: 'replace',
    clearOnDefault: true,
    throttleMs: 0,
  });

  const mode = urlParams.mode;
  const rawDateFrom = urlParams.from
    ? parseAsReportDate.serialize(urlParams.from)
    : null;
  const rawDateTo = urlParams.to
    ? parseAsReportDate.serialize(urlParams.to)
    : null;
  const dateFrom = mode === DATE_RANGE_MODE ? rawDateFrom : null;
  const dateTo = mode === DATE_RANGE_MODE ? rawDateTo : null;

  const clinicId = urlParams.clinic;
  const diagnosis = urlParams.diagnosis;

  const sortBy = urlParams.sort ?? undefined;
  const sortOrder = sortBy ? (urlParams.dir ?? 'asc') : undefined;

  const page = urlParams.page <= 0 ? 1 : urlParams.page;
  const pageSize = coerceReportPageSize(urlParams.size);

  const params = useMemo<RehospitalizationReportParams>(
    () => ({
      mode,
      dateFrom,
      dateTo,
      clinicId,
      diagnosis,
      sortBy,
      sortOrder,
      page,
      pageSize,
    }),
    [
      mode,
      dateFrom,
      dateTo,
      clinicId,
      diagnosis,
      sortBy,
      sortOrder,
      page,
      pageSize,
    ]
  );

  // Date range validation
  const dateValidationError = useMemo(() => {
    if (mode !== DATE_RANGE_MODE) {
      return null;
    }
    if (!dateFrom || !dateTo) {
      return 'Select both From and To dates.';
    }
    if (isInvalidDateRange(dateFrom, dateTo)) {
      return 'From date must be on or before the To date.';
    }
    return null;
  }, [mode, dateFrom, dateTo]);

  const isValidRequest = dateValidationError === null;
  const isBlocked = !isValidRequest;

  // Queries
  const exportQuery = useMemo<RehospitalizationExportQuery>(() => {
    const query: RehospitalizationExportQuery = {
      mode,
    };
    if (mode === DATE_RANGE_MODE) {
      if (dateFrom) query.date_from = dateFrom;
      if (dateTo) query.date_to = dateTo;
    }
    if (clinicId) query.clinic_id = clinicId;
    if (diagnosis.length > 0) query.diagnosis = diagnosis;
    if (sortBy) query.sort_by = sortBy;
    if (sortOrder) query.sort_order = sortOrder;
    return query;
  }, [mode, dateFrom, dateTo, clinicId, diagnosis, sortBy, sortOrder]);

  const tableQuery = useMemo<RehospitalizationTableQuery>(
    () => ({
      ...exportQuery,
      page,
      page_size: pageSize,
    }),
    [exportQuery, page, pageSize]
  );

  // Diagnosis input and debouncing
  const [diagnosisInput, setDiagnosisInput] = useState(() =>
    diagnosis.join(', ')
  );

  const commitDiagnosis = useCallback(
    (inputValue: string) => {
      const parsedCodes = parseDiagnosisInputCodes(inputValue);
      return setUrlParams({
        diagnosis: parsedCodes,
        page: null,
      });
    },
    [setUrlParams]
  );

  const debouncedCommitDiagnosis = useDebounce(commitDiagnosis, 300);

  // Sync external changes: only overwrite diagnosisInput when parsed codes differ from params.diagnosis
  const [prevCanonicalDiagnosis, setPrevCanonicalDiagnosis] = useState(() =>
    diagnosis.join(', ')
  );
  useEffect(() => {
    const canonical = diagnosis.join(', ');
    if (canonical !== prevCanonicalDiagnosis) {
      setPrevCanonicalDiagnosis(canonical);
      const currentInputCodes = parseDiagnosisInputCodes(diagnosisInput);
      if (!areDiagnosisCodesEqual(currentInputCodes, diagnosis)) {
        debouncedCommitDiagnosis.cancel();
        setDiagnosisInput(canonical);
      }
    }
  }, [
    diagnosis,
    diagnosisInput,
    debouncedCommitDiagnosis,
    prevCanonicalDiagnosis,
  ]);

  const handleSetDiagnosisInput = useCallback(
    (value: string) => {
      setDiagnosisInput(value);
      debouncedCommitDiagnosis(value);
    },
    [debouncedCommitDiagnosis]
  );

  // Compute isDiagnosisPending by comparing normalized/parsed codes of diagnosisInput against params.diagnosis
  const isDiagnosisPending = useMemo(() => {
    const inputCodes = parseDiagnosisInputCodes(diagnosisInput);
    return !areDiagnosisCodesEqual(inputCodes, diagnosis);
  }, [diagnosisInput, diagnosis]);

  // Setters
  const setMode = useCallback(
    (newMode: RehospitalizationMode) => {
      if (newMode === DATE_RANGE_MODE) {
        return setUrlParams({
          mode: DATE_RANGE_MODE,
          page: null,
        });
      }
      return setUrlParams({
        mode: CURRENT_MODE,
        from: null,
        to: null,
        page: null,
      });
    },
    [setUrlParams]
  );

  const setDateRange = useCallback(
    (from: string | null, to: string | null) =>
      setUrlParams({
        from: from ? parseAsReportDate.parse(from) : null,
        to: to ? parseAsReportDate.parse(to) : null,
        page: null,
      }),
    [setUrlParams]
  );

  const setClinicId = useCallback(
    (clinic: string | null) =>
      setUrlParams({
        clinic: clinic ?? '',
        page: null,
      }),
    [setUrlParams]
  );

  const setSorting = useCallback(
    (newSortBy?: RehospitalizationSortBy, newSortOrder?: SortOrder) =>
      setUrlParams({
        sort: newSortBy ?? null,
        dir: newSortBy ? (newSortOrder ?? 'asc') : null,
        page: null,
      }),
    [setUrlParams]
  );

  const setPagination = useCallback(
    (newPage: number, newPageSize: number) =>
      setUrlParams({
        page: newPage,
        size: newPageSize,
      }),
    [setUrlParams]
  );

  const reset = useCallback(() => {
    debouncedCommitDiagnosis.cancel();
    setDiagnosisInput('');
    return setUrlParams({
      mode: null,
      from: null,
      to: null,
      clinic: null,
      diagnosis: null,
      sort: null,
      dir: null,
      page: null,
      size: null,
    });
  }, [debouncedCommitDiagnosis, setUrlParams]);

  return {
    params,
    tableQuery,
    exportQuery,
    diagnosisInput,
    setDiagnosisInput: handleSetDiagnosisInput,
    isDiagnosisPending,
    dateValidationError,
    isValidRequest,
    isBlocked,
    setMode,
    setDateRange,
    setClinicId,
    setSorting,
    setPagination,
    reset,
  };
}
